import { useState } from "react";
import "./MarketingDemoSection.css";

const demos = [
  { code: "en", language: "English", nativeName: "English", file: "english", duration: "1:29" },
  { code: "ta", language: "Tamil", nativeName: "தமிழ்", file: "tamil", duration: "1:55" },
  { code: "ml", language: "Malayalam", nativeName: "മലയാളം", file: "malayalam", duration: "1:24" },
  { code: "kn", language: "Kannada", nativeName: "ಕನ್ನಡ", file: "kannada", duration: "1:29" },
  { code: "te", language: "Telugu", nativeName: "తెలుగు", file: "telugu", duration: "1:51" },
  { code: "hi", language: "Hindi", nativeName: "हिन्दी", file: "hindi", duration: "1:25" },
] as const;

type Demo = (typeof demos)[number];

function DemoPlayer({ demo }: { demo: Demo }) {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const source = `${import.meta.env.BASE_URL}demos/leadcat-${demo.file}.mp4`;

  return (
    <div className="pm-demo-player" aria-busy={loading}>
      <video
        key={attempt}
        aria-label={`LeadCat demo in ${demo.language}`}
        controls
        playsInline
        preload="none"
        poster={`${import.meta.env.BASE_URL}demos/leadcat-${demo.file}.jpg`}
        src={source}
        onPlay={(event) => setLoading(event.currentTarget.readyState < 3)}
        onWaiting={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onPlaying={() => setLoading(false)}
        onError={() => { setLoading(false); setFailed(true); }}
      >
        Your browser cannot play this video. <a href={source}>Open the {demo.language} demo</a>.
      </video>
      {loading && !failed && (
        <div className="pm-demo-loading" role="status">
          <span className="pm-demo-spinner" aria-hidden="true" /> Loading {demo.language} demo…
        </div>
      )}
      {failed && (
        <div className="pm-demo-error">
          <p role="alert">This video couldn’t load. Try again or choose another language.</p>
          <button
            type="button"
            className="pm-button pm-button-secondary"
            onClick={() => { setFailed(false); setLoading(false); setAttempt(attempt + 1); }}
          >
            Try again
          </button>
          <a href={source} target="_blank" rel="noreferrer">Open the {demo.language} video</a>
        </div>
      )}
    </div>
  );
}

export function MarketingDemoSection() {
  const [selected, setSelected] = useState<Demo>(demos[0]);

  return (
    <section className="pm-demos" id="demo" aria-labelledby="pm-demo-title">
      <header className="pm-section-heading">
        <span>See LeadCat in action</span>
        <h2 id="pm-demo-title">Watch LeadCat in your language</h2>
        <p>Choose your language for a quick tour of how LeadCat helps your team capture enquiries and follow up.</p>
      </header>
      <div className="pm-demo-languages" role="group" aria-label="Demo language">
        {demos.map((demo) => (
          <button
            key={demo.code}
            type="button"
            className="pm-demo-language"
            aria-label={demo.language}
            aria-pressed={selected.code === demo.code}
            onClick={() => setSelected(demo)}
          >
            <span lang={demo.code}>{demo.nativeName}</span>
            {demo.code !== "en" && <small>{demo.language}</small>}
          </button>
        ))}
      </div>
      <DemoPlayer key={selected.code} demo={selected} />
      <footer className="pm-demo-footer">
        <div aria-live="polite">
          <strong>{selected.language} demo <span aria-hidden="true">·</span> {selected.duration}</strong>
          <span>Press play to watch. Switch languages any time.</span>
        </div>
        <a className="pm-button pm-button-primary" href="/react/signup">
          Get Started <span aria-hidden="true">→</span>
        </a>
      </footer>
    </section>
  );
}
